// ── V&A Brand Tokens ──────────────────────────────────────────────────────────

// Navigation / header background
export const VA_BLACK   = '#0a0a0a';

// Primary accent (buttons, active states, focus)
export const AMBER      = '#d97706';
export const AMBER_DARK = '#b45309';

// Text / headings (slate-900)
export const NAVY       = '#1e293b';
export const NAVY_DARK  = '#0f172a';
export const NAVY_LIGHT = '#f1f5f9';  // slate-100 (light bg)

// Page & field backgrounds
export const BG_PAGE    = '#f8fafc';  // slate-50
export const BG_FIELD   = '#f1f5f9';  // slate-100

// Borders
export const BORDER_COLOR = '#e2e8f0'; // slate-200

// ── Global CSS ────────────────────────────────────────────────────────────────

export const BASE_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body, input, select, button, textarea {
    font-family: Inter, ui-sans-serif, system-ui, sans-serif;
  }

  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #f1f5f9; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

  input:focus, select:focus, textarea:focus {
    outline: 2px solid #f59e0b;
    outline-offset: 1px;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50%       { opacity: 0.8; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  .spin { animation: spin 0.9s linear infinite; display: inline-block; }

  /* ── Responsive utilities ── */
  .mobile-only { display: none !important; }
  .desktop-only { display: block !important; }

  /* Bottom tab bar — hidden by default, shown on mobile */
  .bottom-tab-bar {
    display: none;
    position: fixed; bottom: 0; left: 0; right: 0;
    height: 60px;
    background: #0a0a0a;
    border-top: 1px solid rgba(255,255,255,0.08);
    z-index: 200;
    justify-content: space-around;
    align-items: center;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
  .bottom-tab-btn {
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    background: none; border: none; cursor: pointer;
    color: rgba(255,255,255,0.45);
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.5px; text-transform: uppercase;
    padding: 8px 20px;
    transition: color 0.15s;
    font-family: Inter, ui-sans-serif, sans-serif;
    flex: 1;
    min-width: 0;
  }
  .bottom-tab-btn.active { color: #d97706; }
  .bottom-tab-btn-icon { font-size: 22px; line-height: 1; }

  @media (max-width: 640px) {
    .mobile-only  { display: block !important; }
    .desktop-only { display: none !important; }

    /* Bottom tab bar */
    .bottom-tab-bar { display: flex !important; }

    /* Add bottom padding so content isn't hidden behind tab bar */
    .has-bottom-bar { padding-bottom: 72px !important; }

    /* Navbar: hide user name and role badge on mobile */
    .navbar-name { display: none !important; }
    .navbar-role { display: none !important; }

    /* Page padding: tighter on mobile */
    .page-pad { padding: 16px !important; }

    /* Stat cards: 2×2 grid */
    .stat-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 10px !important;
    }
    .stat-grid > * { flex: unset !important; min-width: unset !important; }

    /* Filter pills: horizontal scroll, no wrap */
    .filter-bar {
      overflow-x: auto !important;
      flex-wrap: nowrap !important;
      padding-bottom: 4px;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .filter-bar::-webkit-scrollbar { display: none; }

    /* Date range: stack vertically */
    .date-range { flex-direction: column !important; align-items: flex-start !important; }
    .date-range input { width: 100% !important; }

    /* Action buttons: full-width stacked */
    .action-btns { flex-direction: column !important; }
    .action-btns > * { width: 100% !important; text-align: center; }

    /* Staging table: hide header, stack rows as cards */
    .staging-header { display: none !important; }
    .staging-row {
      display: flex !important;
      flex-direction: column !important;
      gap: 8px !important;
      padding: 14px 16px !important;
    }
    .staging-row-preview { align-self: flex-start; }

    /* Draft card field grid: 2-col on mobile */
    .receipt-form-grid {
      grid-template-columns: 1fr 1fr !important;
    }

    /* Supervisor table → hide on mobile */
    .supervisor-table { display: none !important; }

    /* Supervisor mobile cards → show on mobile */
    .supervisor-mobile-cards { display: flex !important; }

    /* Upload zone: bigger tap target text */
    .upload-zone-title { font-size: 17px !important; }

    /* Card actions: move below content on mobile */
    .card-actions {
      flex-direction: row !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
      width: 100% !important;
      align-items: center !important;
    }
    .card-actions > button { flex: 1 !important; min-width: 120px !important; justify-content: center; }

    /* Dashboard charts: stack on mobile */
    .dashboard-grid { grid-template-columns: 1fr !important; }
  }

  /* Dashboard chart grid: 3-col on desktop, compact */
  .dashboard-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 14px;
  }
`;

// ── Style Objects ─────────────────────────────────────────────────────────────

export const bubbleStyle = {
  background: '#fff',
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: 16,
  boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
  padding: '20px 24px',
};

export const navyBtn = {
  background: AMBER,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 20px',
  fontWeight: '600',
  fontSize: 14,
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
  transition: 'background 0.15s',
};

export const navyBtnHover = {
  background: AMBER_DARK,
};

export const outlineBtn = {
  background: 'transparent',
  color: NAVY,
  border: `1.5px solid ${BORDER_COLOR}`,
  borderRadius: 8,
  padding: '8px 16px',
  fontWeight: '600',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};

export const fieldStyle = {
  background: BG_FIELD,
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: 6,
  padding: '7px 10px',
  fontSize: 13,
  width: '100%',
  fontFamily: 'Inter, sans-serif',
};

export const selectStyle = {
  ...fieldStyle,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  paddingRight: 28,
};
